import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Coupon } from 'src/app/core/interfaces/coupon';
import { HttpRequestService } from 'src/app/core/services';

@Component({
  selector: 'app-add-update-coupon',
  templateUrl: './add-update-coupon.component.html',
  styleUrls: ['./add-update-coupon.component.scss'],
})
export class AddUpdateCouponComponent implements OnInit {
  addUpdateCouponForm: FormGroup;
  idForUpdate: string;
  buttonLoading = false;
  pageSize = 10;
  pageIndex = 1;
  checkAddPermission = false;
  checkUpdatePermission = false;
  totalPlanDataCount: number = 0;
  searchPlans: Subject<string> = new Subject<string>();
  planSearch: string = '';
  listOfPlans: any[] = [];
  tabIndex: number = 0;
  displayValueRupee: boolean = false;
  allDiscountType: any[] = [
    {
      value: 'PERCENTAGE',
      label: 'Percentage',
    },
    {
      value: 'FLAT',
      label: 'Flat',
    },
  ];
  couponFor: any[] = [
    {
      value: 'GLOBAL',
      label: 'Global',
    },
    {
      value: 'PLAN',
      label: 'Plan',
    },
    {
      value: 'TEST_SERIES',
      label: 'Test Series',
    },
    {
      value: 'USER',
      label: 'User',
    },
  ];
  totalSeriesDataCount: number = 0;
  searchSeries: Subject<string> = new Subject<string>();
  seriesSearch: string = '';
  listOfSeries: any[] = [];
  couponForValue: string = '';

  totalUserDataCount: number = 0;
  searchUser: Subject<string> = new Subject<string>();
  userSearch: string = '';
  listOfUser: any[] = [];
  loadingPlans = false;
  loadingSeries = false;
  loadingUsers = false;
  planPageIndex = 1;
  seriesPageIndex = 1;
  userPageIndex = 1;
  constructor(
    private fb: FormBuilder,
    private httpRequestService: HttpRequestService,
    private notificationService: NzNotificationService,
    private activatedRoute: ActivatedRoute,
    private router: Router
  ) {
    this.tabIndex = this.activatedRoute.snapshot.queryParams.index;
    this.idForUpdate = this.activatedRoute.snapshot.params.id;
    this.addUpdateCouponForm = this.fb.group({
      couponTitle: [null, [Validators.required]],
      couponCode: [null, [Validators.required]],
      discountType: [null, [Validators.required]],
      discountValue: [null, [Validators.required]],
      startDate: [null, [Validators.required]],
      endDate: [null, [Validators.required]],
      distributeLimit: [null],
      maxDiscountValue: [null, [Validators.required]],
      applyForCoupon: ['GLOBAL', [Validators.required]],
      plans: [[]],
      testSeries: [[]],
      description: [null, [Validators.required]],
      users: [[]],
    });
  }

  // on page load functions
  ngOnInit(): void {
    this.searchPlans.pipe(debounceTime(1000)).subscribe((success: any) => {
      this.planSearch = success;
      this.getAllPlans();
    });

    this.searchSeries.pipe(debounceTime(1000)).subscribe((success: any) => {
      this.seriesSearch = success;
      this.getAllSeries();
    });

    this.searchUser.pipe(debounceTime(1000)).subscribe((success: any) => {
      this.userSearch = success;
      this.getAllUsers();
    });

    if (this.idForUpdate) {
      this.getCouponById();
    }
  }

  /* Get single  details by Id */
  getCouponById(): void {
    this.httpRequestService
      .request('get', `coupon-codes/${this.idForUpdate}`)
      .subscribe(
        (result: any) => {
          const data: Coupon = result.data;
          // Normalize relation fields to arrays of ids so nz-select (tags mode)
          // renders tags correctly instead of showing "[object Object]".
          const plans = Array.isArray(data.plans)
            ? data.plans.map((p: any) => (p && p._id ? p._id : p))
            : [];
          const testSeries = Array.isArray(data.testSeries)
            ? data.testSeries.map((s: any) => (s && s._id ? s._id : s))
            : [];
          const users = Array.isArray(data.users)
            ? data.users.map((u: any) => (u && u._id ? u._id : u))
            : [];

          // If the API returned full objects for related lists (with name/_id),
          // populate the local option lists so nz-select can show the labels
          // for the currently selected ids. Merge to avoid duplicates.
          if (Array.isArray(data.plans) && data.plans.length) {
            const planObjs = data.plans.filter(
              (p: any) => p && (p._id || p.name)
            );
            this.listOfPlans = [
              ...new Map(
                [...this.listOfPlans, ...planObjs].map((i: any) => [i._id, i])
              ).values(),
            ];
          }
          if (Array.isArray(data.testSeries) && data.testSeries.length) {
            const seriesObjs = data.testSeries.filter(
              (s: any) => s && (s._id || s.name)
            );
            this.listOfSeries = [
              ...new Map(
                [...this.listOfSeries, ...seriesObjs].map((i: any) => [
                  i._id,
                  i,
                ])
              ).values(),
            ];
          }
          if (Array.isArray(data.users) && data.users.length) {
            const userObjs = data.users.filter(
              (u: any) => u && (u._id || u.name)
            );
            this.listOfUser = [
              ...new Map(
                [...this.listOfUser, ...userObjs].map((i: any) => [i._id, i])
              ).values(),
            ];
          }

          this.addUpdateCouponForm.patchValue({
            couponTitle: data.couponTitle,
            couponCode: data.couponCode,
            discountType: data.discountType,
            discountValue: data.discountValue,
            startDate: data.startDate,
            endDate: data.endDate,
            distributeLimit: data.distributeLimit,
            maxDiscountValue: data.maxDiscountValue,
            applyForCoupon: data.applyForCoupon,
            plans: plans,
            testSeries: testSeries,
            description: data.description,
            users: users,
          });
        },
        (error: any) => {}
      );
  }
  loadMorePlans(): void {
    if (this.totalPlanDataCount > this.listOfPlans.length) {
      this.planPageIndex++;
      const skip = this.pageSize * (this.planPageIndex - 1);
      this.getAllPlans(skip);
    }
  }

  loadMoreSeries(): void {
    if (this.totalSeriesDataCount > this.listOfSeries.length) {
      this.seriesPageIndex++;
      const skip = this.pageSize * (this.seriesPageIndex - 1);
      this.getAllSeries(skip);
    }
  }

  loadMoreUsers(): void {
    if (this.totalUserDataCount > this.listOfUser.length) {
      this.userPageIndex++;
      const skip = this.pageSize * (this.userPageIndex - 1);
      this.getAllUsers(skip);
    }
  }

  // Search functions should reset pagination
  onSearch(event: any): void {
    this.listOfPlans = [];
    this.planPageIndex = 1;
    this.searchPlans.next(event);
  }

  onSearchSeries(event: any): void {
    this.listOfSeries = [];
    this.seriesPageIndex = 1;
    this.searchSeries.next(event);
  }

  onSearchUser(event: any): void {
    this.listOfUser = [];
    this.userPageIndex = 1;
    this.searchUser.next(event);
  }

  /* Get all Plans - Updated with proper pagination handling */
  getAllPlans(skip = 0): void {
    this.loadingPlans = true;
    let params: any = { skip, limit: this.pageSize, approveStatus: 'approved' };

    if (this.planSearch) {
      params.search = this.planSearch;
    }

    this.httpRequestService.request('get', 'plans', params).subscribe(
      (result: any) => {
        this.loadingPlans = false;
        if (skip === 0 || this.planSearch !== '') {
          // New search or initial load - replace data
          this.listOfPlans = result.data;
        } else {
          // Load more - append data
          this.listOfPlans = [...this.listOfPlans, ...result.data];
        }
        this.totalPlanDataCount = result.totalCount;
      },
      (err: any) => {
        this.loadingPlans = false;
      }
    );
  }

  /* Get all Series - Updated with proper pagination handling */
  getAllSeries(skip = 0): void {
    this.loadingSeries = true;
    let params: any = { skip, limit: this.pageSize, approveStatus: 'approved' };

    if (this.seriesSearch) {
      params.search = this.seriesSearch;
    }

    this.httpRequestService
      .request('get', 'test-series/for-list', params)
      .subscribe(
        (result: any) => {
          this.loadingSeries = false;
          if (skip === 0 || this.seriesSearch !== '') {
            // New search or initial load - replace data
            this.listOfSeries = result.data;
          } else {
            // Load more - append data
            this.listOfSeries = [...this.listOfSeries, ...result.data];
          }
          this.totalSeriesDataCount = result.totalCount;
        },
        (err: any) => {
          this.loadingSeries = false;
        }
      );
  }

  /* Get all Users - Updated with proper pagination handling */
  getAllUsers(skip = 0): void {
    this.loadingUsers = true;
    let params: any = { skip, limit: this.pageSize };

    if (this.userSearch) {
      params.search = this.userSearch;
    }

    this.httpRequestService.request('get', 'users', params).subscribe(
      (result: any) => {
        this.loadingUsers = false;
        if (skip === 0 || this.userSearch !== '') {
          // New search or initial load - replace data
          this.listOfUser = result.data;
        } else {
          // Load more - append data
          this.listOfUser = [...this.listOfUser, ...result.data];
        }
        this.totalUserDataCount = result.totalCount;
      },
      (err: any) => {
        this.loadingUsers = false;
      }
    );
  }

  /* Submit  form */
  submit(): void {
    if (!this.addUpdateCouponForm.valid) {
      this.markFormGroupTouched(this.addUpdateCouponForm);
    } else {
      const startDate = new Date(this.addUpdateCouponForm.value.startDate);
      const endDate = new Date(this.addUpdateCouponForm.value.endDate);
      if (endDate >= startDate) {
        if (this.idForUpdate) {
          this.addOrUpdateCoupon(
            'put',
            `coupon-codes/${this.idForUpdate}`,
            'Coupon Successfully Updated'
          );
        } else {
          this.addOrUpdateCoupon(
            'post',
            'coupon-codes',
            'Coupon Added Successfully '
          );
        }
      } else {
        this.notificationService.error(
          '',
          'Start date should be same or less than end date'
        );
        return;
      }
    }
  }
  onCouponInput(event: Event) {
    const input = event.target as HTMLInputElement;
    input.value = input.value.toUpperCase(); // update UI
    this.addUpdateCouponForm
      .get('couponCode')
      ?.setValue(input.value, { emitEvent: false }); // update form value
  }

  /* Add Or Edit Subject */
  addOrUpdateCoupon(
    requestMethod: string,
    requestURL: string,
    successMessage: string
  ): void {
    this.buttonLoading = true;
    const finalData: Coupon = this.addUpdateCouponForm.value;
    this.httpRequestService
      .request(requestMethod, requestURL, finalData)
      .subscribe(
        (result: any) => {
          this.notificationService.success('', successMessage);
          this.router.navigate(['/main/coupons'], {
            queryParams: { index: this.tabIndex },
          });
          this.buttonLoading = false;
        },
        (error: any) => {
          if (error.error.errors) {
            const allErrors: string[] = Object.values(error.error.errors);
            for (const err of allErrors) {
              this.notificationService.error('', err);
            }
          } else {
            this.notificationService.error('', error.error.message);
          }
          this.buttonLoading = false;
        }
      );
  }

  /* Make All Form Controls Dirty */
  private markFormGroupTouched(formGroup: FormGroup): void {
    for (const i in formGroup.controls) {
      if (formGroup.controls.hasOwnProperty(i)) {
        formGroup.controls[i].markAsDirty();
        formGroup.controls[i].updateValueAndValidity();
      }
    }
  }
  /* Check if control is valid  and  select discount type*/
  onDiscountTypeChange(value: string): void {
    const isFlat = value === 'FLAT';
    this.displayValueRupee = isFlat;

    const control = this.addUpdateCouponForm.get('maxDiscountValue')!;
    control.setValue(isFlat ? 0 : null);

    if (isFlat) {
      control.disable({ emitEvent: false });
    } else {
      control.enable({ emitEvent: false });
      control.setValidators([Validators.required]);
    }
  }

  onCouponForChange(value: string): void {
    this.couponForValue = value;
    const controlPlans = this.addUpdateCouponForm.get('plans')!;
    const controlSeries = this.addUpdateCouponForm.get('testSeries')!;
    const controlUsers = this.addUpdateCouponForm.get('users')!;
    controlPlans.setValue(null);
    controlSeries.setValue(null);
    controlUsers.setValue(null);
  }
}
